import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  FC,
} from "react";
import { useKiosk } from "../kiosk/KioskContext";
import { useAuth } from "../auth/AuthContext";
import { useUi } from "../ui/UIContext";
import { useHistory } from "react-router-dom";
import { SessionService } from "../../../domains/admin/services/SessionService";
import { getGlobalAddLog } from "Infrastructure/repositories/appInsights/AppInsights";

const addLog = getGlobalAddLog();
const sessionService = SessionService();

export interface ISessionContext {
  sessionIsOpen: boolean;
  closeSession: () => Promise<void>;
  openSession: () => Promise<string | undefined>;
  exitSession: (options?: { silent?: boolean }) => Promise<boolean>;
  verifySessionIsOpen: () => Promise<boolean>;
  sessaoIdPos: string | undefined;
}

const SessionContext = createContext<ISessionContext>({} as ISessionContext);

export const SessionProvider: FC = ({ children }) => {
  const { isAuth } = useAuth();
  const { toast, toastFullscreen, showLoading, hideLoading } = useUi();
  const { kiosk, syncKiosk } = useKiosk();
  const history = useHistory();

  const [sessionIsOpen, setsessionIsOpen] = useState(false);
  const [sessaoIdPos, setSessaoIdPos] = useState<string>();

  const getSession = useCallback(async () => {
    try {
      if (kiosk && isAuth) {
        const response = await sessionService.getSession(kiosk.localId);
        setsessionIsOpen(response.possuiSessaoAberta);
        setSessaoIdPos(response.sessaoPOSId);
        return response;
      }
    } catch (error) {
      addLog && addLog("getSession", { error });
    }
  }, [isAuth, kiosk]);

  const verifySessionIsOpen = useCallback(async () => {
    try {
      const sessaoAberta = await getSession();
      if (!sessaoAberta?.possuiSessaoAberta) {
        toast("Fechado para consumo / Sem conexão com internet", "error");
        history.replace("/");
        return false;
      } else {
        return true;
      }
    } catch (error) {
      const errorMessage = (error as any)?.response?.data?.message ?? undefined;
      addLog && addLog("verifySessionIsOpen", { errorMessage, error });
      toast(
        errorMessage ?? (error as any)?.message ?? "Falha ao obter sessão",
        "error"
      );
      return false;
    }
  }, [getSession, history, toast]);

  const closeSession = useCallback(async () => {
    if (kiosk && isAuth) {
      try {
        showLoading();
        await sessionService.closeSession(kiosk.localId);
        toast("Caixa fechado com sucesso", "success");
        getSession();
      } catch (error) {
        const errorMessage =
          (error as any)?.response?.data?.message ?? undefined;
        addLog && addLog("closeSession", { errorMessage, error });
        toast(
          errorMessage ?? (error as any)?.message ?? "Falha ao fechar sessão",
          "error"
        );
      } finally {
        hideLoading();
      }
    }
  }, [getSession, hideLoading, isAuth, kiosk, showLoading, toast]);

  const openSession = useCallback(async () => {
    if (kiosk && isAuth) {
      try {
        showLoading();
        await sessionService.openSession(kiosk.localId);
        toast("Caixa aberto com sucesso", "success");
        const result = await getSession();
        return result?.sessaoPOSId;
      } catch (error) {
        const errorMessage =
          (error as any)?.response?.data?.message ?? undefined;
				addLog && addLog("openSession", { errorMessage, error });
        toast(
          errorMessage ?? (error as any)?.message ?? "Falha ao abrir caixa",
          "error"
        );
      } finally {
        hideLoading();
      }
    }
  }, [getSession, hideLoading, isAuth, kiosk, showLoading, toast]);

  const exitSession = useCallback(
    async (options?: { silent?: boolean }) => {
      if (!kiosk?.registroSessaoPOSId || !isAuth) {
        return true;
      }

      const silent = options?.silent ?? false;
      try {
        !silent && showLoading();
        await sessionService.exitSession(
          kiosk.localId,
          kiosk.registroSessaoPOSId
        );
        if (!silent) {
          toast("Saída da sessão realizada com sucesso", "success");
          await syncKiosk();
        }
        return true;
      } catch (error) {
        const errorMessage =
          (error as any)?.response?.data?.message ?? undefined;
        addLog && addLog("exitSession", { errorMessage, error });
        toast(
          errorMessage ?? (error as any)?.message ?? "Falha ao sair da sessão",
          "error"
        );
        return false;
      } finally {
        !silent && hideLoading();
      }
    },
    [hideLoading, isAuth, kiosk, showLoading, syncKiosk, toast]
  );

  useEffect(() => {
    getSession();
    return () => {};
  }, [getSession]);

  return (
    <SessionContext.Provider
      value={{
        sessionIsOpen,
        closeSession,
        openSession,
        exitSession,
        verifySessionIsOpen,
        sessaoIdPos,
      }}
    >
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  return context;
};
